import { useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Download, Gift, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  registrationSchema,
  checkRateLimit,
  formatRetryAfter,
} from "@/lib/form-security";
import { trackCTA, trackConversion } from "@/lib/tracking";

const TEASERS = [
  { icon: Gift, label: "The MAKECHANGE Playbook (10-pillar PDF)" },
  { icon: Sparkles, label: "7-day Make Change email mini-course" },
  { icon: Download, label: "Free chapter of Built On Purpose" },
];

type Props = {
  sourcePage?: string;
  compact?: boolean;
  className?: string;
};

export const LeadMagnetForm = ({
  sourcePage = "homepage",
  compact = false,
  className,
}: Props) => {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", website: "" });
  const [errors, setErrors] = useState<{ full_name?: string; email?: string }>({});
  const startedAt = useRef<number>(Date.now());

  const validateField = (field: "full_name" | "email") => {
    const r = registrationSchema.safeParse(form);
    setErrors((prev) => {
      const next = { ...prev };
      if (r.success) {
        delete next[field];
        return next;
      }
      const issue = r.error.issues.find((i) => i.path[0] === field);
      if (issue) next[field] = issue.message;
      else delete next[field];
      return next;
    });
  };

  const clearError = (field: "full_name" | "email") =>
    setErrors((p) => {
      if (!p[field]) return p;
      const n = { ...p };
      delete n[field];
      return n;
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Intent-level CTA event on every submit attempt (fires before any
    // validation / honeypot / rate-limit gates). Pairing this with the
    // downstream `masterclass_register_submit` conversion lets us measure
    // form-abandonment vs. successful masterclass registrations.
    trackCTA("masterclass_register_submit_click", { source_page: sourcePage });

    // Honeypot — bots fill hidden fields
    if (form.website) return;

    // Min-fill timer — bots submit instantly
    if (Date.now() - startedAt.current < 2500) {
      toast.error("Please take a moment to fill the form.");
      return;
    }

    const r = registrationSchema.safeParse(form);
    if (!r.success) {
      const fieldErrors: { full_name?: string; email?: string } = {};
      for (const issue of r.error.issues) {
        const key = issue.path[0] as "full_name" | "email";
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      toast.error(r.error.issues[0]?.message ?? "Check your details");
      // Focus first invalid field
      const firstKey = r.error.issues[0]?.path[0];
      if (firstKey) {
        const el = document.getElementById(`lm-${firstKey === "full_name" ? "name" : "email"}-${sourcePage}`);
        (el as HTMLInputElement | null)?.focus();
      }
      return;
    }
    setErrors({});

    // Client-side rate limit: 5 attempts per hour, per browser, per bucket.
    // Not a security boundary — the real backstop is RLS + unique email.
    const rl = checkRateLimit({ bucket: `lead-magnet:${sourcePage}`, max: 5, windowMs: 60 * 60 * 1000 });
    if (!rl.ok) {
      toast.error(`Too many attempts. Try again in ${formatRetryAfter(rl.retryAfterMs)}.`);
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("lead_magnet_subscribers").insert({
      email: r.data.email, // already lower-cased + trimmed by the schema
      full_name: r.data.full_name,
      source_page: sourcePage,
      tags: ["playbook", "mini-course", "book-chapter"],
    });
    setSubmitting(false);

    if (error && error.code !== "23505") {
      console.error("lead_magnet_subscribers insert error:", error);
      toast.error("Something went wrong. Please try again.");
      return;
    }

    if (error?.code === "23505") {
      toast.success("You're already on the list — check your inbox for your downloads.");
    }
    // Fire to GA4 / Meta Pixel / dataLayer / internal funnel. Duplicates (23505)
    // are still tracked as 'resubscribe' so we don't undercount real interest.
    trackConversion("lead_magnet_success", {
      source_page: sourcePage,
      duplicate: error?.code === "23505",
    });
    // Dedicated masterclass-registration conversion so the /funnel + GA4
    // dashboards can measure masterclass sign-ups independently of other
    // lead-magnet placements (e.g. footer, blog inline).
    trackConversion("masterclass_register_submit", {
      source_page: sourcePage,
      duplicate: error?.code === "23505",
    });
    setDone(true);
  };

  if (done) {
    return (
      <div
        className={cn(
          "border border-gold/40 bg-gradient-to-br from-gold/[0.08] to-navy p-6 md:p-8 text-center",
          className,
        )}
      >
        <div className="relative inline-flex">
          <div className="absolute inset-0 bg-gold/30 blur-2xl rounded-full" />
          <CheckCircle2 className="relative h-12 w-12 text-gold mx-auto" />
        </div>
        <h3 className="font-display uppercase text-white text-xl md:text-2xl mt-5">
          You're <span className="text-gradient-gold">in.</span>
        </h3>
        <p className="text-silver text-sm mt-3 max-w-md mx-auto leading-relaxed">
          Check your inbox in the next few minutes for the MAKECHANGE Playbook, the
          first day of the email mini-course, and the free chapter of
          <em> Built On Purpose</em>.
        </p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold/80 mt-5">
          Sent from DrSully@DrSullySpeaks.com
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "border border-gold/30 bg-navy/90 backdrop-blur-sm p-6 md:p-8 shadow-[0_20px_60px_-25px_hsl(var(--gold)/0.3)]",
        className,
      )}
      noValidate
    >
      {!compact && (
        <>
          <div className="inline-flex items-center gap-2 bg-orange/10 border border-orange/30 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-orange">
            <Gift className="h-3 w-3" /> Free · 3 Teasers
          </div>
          <h3 className="font-display font-bold uppercase text-white text-2xl md:text-3xl mt-4 leading-tight">
            Get the <span className="text-gradient-gold">MAKECHANGE</span> starter kit.
          </h3>
          <p className="text-silver text-sm mt-3 leading-relaxed">
            One email. Three downloads. The same framework Dr. Sully uses with
            500+ leaders, founders, and pastors.
          </p>

          <ul className="mt-5 space-y-2.5">
            {TEASERS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-sm text-white/90">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gold/15 text-gold flex-shrink-0">
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {label}
              </li>
            ))}
          </ul>
        </>
      )}

      <div className={cn("space-y-4", compact ? "" : "mt-6")}>
        {/* Honeypot */}
        <div className="absolute -left-[9999px] opacity-0" aria-hidden>
          <Label htmlFor={`website-${sourcePage}`}>Website</Label>
          <input
            id={`website-${sourcePage}`}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => setForm((s) => ({ ...s, website: e.target.value }))}
          />
        </div>

        <div>
          <Label
            htmlFor={`lm-name-${sourcePage}`}
            className="text-[10px] uppercase tracking-[0.25em] text-gold"
          >
            Your Name
          </Label>
          <Input
            id={`lm-name-${sourcePage}`}
            className={cn(
              "mt-2 bg-navy-elevated/60 border-border text-white placeholder:text-silver/50 focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:border-gold/60",
              errors.full_name && "border-red-500/70 focus-visible:ring-red-500/60 focus-visible:border-red-500/70",
            )}
            value={form.full_name}
            onChange={(e) => {
              setForm((s) => ({ ...s, full_name: e.target.value }));
              clearError("full_name");
            }}
            onBlur={() => validateField("full_name")}
            aria-invalid={!!errors.full_name}
            aria-describedby={errors.full_name ? `lm-name-${sourcePage}-error` : undefined}
            maxLength={120}
            placeholder="Jane Doe"
            required
          />
          {errors.full_name && (
            <p
              id={`lm-name-${sourcePage}-error`}
              role="alert"
              className="text-xs text-red-400 mt-1.5"
            >
              {errors.full_name}
            </p>
          )}
        </div>
        <div>
          <Label
            htmlFor={`lm-email-${sourcePage}`}
            className="text-[10px] uppercase tracking-[0.25em] text-gold"
          >
            Best Email
          </Label>
          <Input
            id={`lm-email-${sourcePage}`}
            type="email"
            className={cn(
              "mt-2 bg-navy-elevated/60 border-border text-white placeholder:text-silver/50 focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:border-gold/60",
              errors.email && "border-red-500/70 focus-visible:ring-red-500/60 focus-visible:border-red-500/70",
            )}
            value={form.email}
            onChange={(e) => {
              setForm((s) => ({ ...s, email: e.target.value }));
              clearError("email");
            }}
            onBlur={() => validateField("email")}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? `lm-email-${sourcePage}-error` : undefined}
            maxLength={255}
            placeholder="jane@example.com"
            required
          />
          {errors.email && (
            <p
              id={`lm-email-${sourcePage}-error`}
              role="alert"
              className="text-xs text-red-400 mt-1.5"
            >
              {errors.email}
            </p>
          )}
        </div>


        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-cta hover:bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] py-6 shadow-cta transition-all"
        >
          {submitting ? "Sending..." : "Send My Free Kit"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>

        <p className="text-[10px] uppercase tracking-[0.2em] text-silver/60 text-center">
          No spam. Unsubscribe in one click. Your email stays with Dr. Sully.
        </p>
      </div>
    </form>
  );
};
